import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';

import {
    IqCssClassnames,
    IqCssClassProcessorDirective,
    IqStyleProcessorShowElementComponent,
} from '@dat/iq-style-processor';
import { ControlLegacySelectPipe } from '../control-legacy-select/control-legacy-select.pipe';
import {
    AddClassPayload,
    AddClassPayloadSchema,
    Control,
    RemoveClassPayload,
    RemoveClassPayloadSchema,
    ToggleClassPayload,
    ToggleClassPayloadSchema,
} from '../models';
import { IqControlTabTabsComponent } from '@dat/iq-control-tab';
import {
    IqControlTextPercentComponent,
    IqControlTextGeoComponent,
    IqControlTextIntegerComponent,
    IqControlTextCurrencyComponent,
} from '@dat/iq-control-text';
import { ControlLegacyTabsPipe } from '../control-legacy-tabs/control-legacy-tabs.pipe';
import { IqControlsActionButtonComponent } from '../iq-controls-action-button/iq-controls-action-button.component';
import { IqControlsLabelComponent } from '../iq-controls-label/iq-controls-label.component';
import { IqControlsResetButtonComponent } from '../iq-controls-reset-button/iq-controls-reset-button.component';
import { IqControlsSubmitButtonComponent } from '../iq-controls-submit-button/iq-controls-submit-button.component';
import { IqControlCheckboxControlComponent } from '@dat/iq-control-checkbox';
import {
    Component,
    inject,
    Input,
    InputSignal,
    input,
    Signal,
    computed,
    Output,
    EventEmitter,
    OnInit,
    effect,
    untracked,
    OnDestroy,
    ElementRef,
} from '@angular/core';
import { IqControlsStatusService } from '../state';
import { DatiqSelectModule } from '@dat/datiq-components-pkg/select';
import {
    IqEventBusBrokerService,
    IqEventBusBrokerHandler,
} from '@dat/iq-event-bus';

@Component({
    selector: 'iq-controls-control-panel-form',
    standalone: true,
    templateUrl: './iq-controls-control-panel-form.component.html',
    styleUrl: './iq-controls-control-panel-form.component.scss',
    imports: [
        CommonModule,
        ControlLegacySelectPipe,
        ControlLegacyTabsPipe,
        IqControlsLabelComponent,
        IqControlsActionButtonComponent,
        DatiqSelectModule,
        IqStyleProcessorShowElementComponent,
        IqCssClassProcessorDirective,
        IqControlTextPercentComponent,
        IqControlsResetButtonComponent,
        IqControlTextGeoComponent,
        IqControlsSubmitButtonComponent,
        IqControlTextIntegerComponent,
        IqControlCheckboxControlComponent,
        IqControlTabTabsComponent,
        IqControlTextCurrencyComponent,
    ],
})
export class IqControlsControlPanelFormComponent implements OnInit, OnDestroy {
    private readonly _fb = inject(FormBuilder);

    @Input()
    fg: FormGroup;

    /* Input Signals */
    id: InputSignal<string> = input('');
    testId: InputSignal<string> = input('');
    name: InputSignal<string> = input('');
    classes: InputSignal<IqCssClassnames> = input({});
    controls: InputSignal<Control[]> = input([]);
    isEdited: InputSignal<boolean> = input(false);

    /* Computed Signals */
    currentTestId: Signal<string> = computed(() => {
        return !!this.testId()?.length ? this.testId() : this.id();
    });

    /* Outputs */
    @Output()
    clicked = new EventEmitter<any>();
    @Output()
    changed = new EventEmitter<any>();
    @Output()
    submitted = new EventEmitter<any>();
    @Output()
    resetted = new EventEmitter<any>();

    private readonly _status = inject(IqControlsStatusService);

    constructor() {
        this._dispatchEvents = this._events.registerMap({
            addPanelFormClass: {
                callback: (payload: AddClassPayload) => {
                    this.addClass(payload);
                },
            },
            removePanelFormClass: {
                callback: (payload: RemoveClassPayload) => {
                    this.removeClass(payload);
                },
            },
            togglePanelFormClass: {
                callback: (payload: ToggleClassPayload) => {
                    this.toggleClass(payload);
                },
            },
        });
    }

    private _events: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _dispatchEvents: Record<string, IqEventBusBrokerHandler> = {};

    private _unregisteredActions: Record<string, any> = {};

    private elementRef: ElementRef = inject(ElementRef);

    addClass(payload: AddClassPayload): void {
        const test: any = AddClassPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('AddClassPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        const elements = payload.selector
            ? this.elementRef.nativeElement.querySelectorAll(payload.selector)
            : [this.elementRef.nativeElement];

        elements.forEach((element: HTMLElement) => {
            payload.classNames
                ?.filter(
                    (className: string) =>
                        className && !element.classList.contains(className),
                )
                .forEach((className: string) => {
                    element.classList.add(className);
                });
        });
    }
    removeClass(payload: RemoveClassPayload): void {
        const test: any = RemoveClassPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('RemoveClassPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        const elements = payload.selector
            ? this.elementRef.nativeElement.querySelectorAll(payload.selector)
            : [this.elementRef.nativeElement];

        elements.forEach((element: HTMLElement) => {
            payload.classNames
                ?.filter(
                    (className: string) =>
                        className && element.classList.contains(className),
                )
                ?.forEach((className: string) => {
                    element.classList.remove(className);
                });
        });
    }
    toggleClass(payload: ToggleClassPayload): void {
        const test: any = ToggleClassPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('ToggleClassPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        const elements = payload.selector
            ? this.elementRef.nativeElement.querySelectorAll(payload.selector)
            : [this.elementRef.nativeElement];

        elements.forEach((element: HTMLElement) => {
            payload.classNames?.forEach((className: string) => {
                element.classList.contains(className)
                    ? element.classList.remove(className)
                    : element.classList.add(className);
            });
        });
    }

    ngOnDestroy() {
        this._events.unregisterMap(this._dispatchEvents);
    }

    ngOnInit() {}

    click(...args): void {
        const emitData = this.createEmitData(args);

        this.clicked.emit(emitData);
    }

    change(...args) {
        const emitData = this.createEmitData(args);

        this.changed.emit(emitData);
    }

    submit(...args) {
        const emitData = this.createEmitData(args);
        this.submitted.emit(emitData);
    }

    reset(...args) {
        const emitData = this.createEmitData(args);
        this.resetted.emit(emitData);
    }

    private createEmitData(args: any[]) {
        const templateProps: Record<any, number> =
            args?.reduce(
                (
                    currentProps: Record<any, number>,
                    key: any,
                    index: number,
                ) => {
                    return index % 2 === 0
                        ? {
                              ...currentProps,
                              [key]: args?.[index + 1] ?? null,
                          }
                        : currentProps;
                },
                {},
            ) ?? {};
        const emitData = {
            form: {
                ...this.fg.value,
                valid: this.fg.valid,
                errors: this.fg.errors,
            },
            ...templateProps,
        };
        return emitData;
    }
}
